<template>
  <main-template>
    <v-row>
      <v-col>
        <h1>
          <v-btn
            id="historico-voltar"
            data-testid="historico-voltar"
            color="primary"
            text
            x-small
            @click="handleBackNavigation"
          >
            <v-icon left>fa fa-angle-left</v-icon>
          </v-btn>
          {{ fullTitle }}
        </h1>
      </v-col>
    </v-row>

    <v-form ref="form" lazy-validation @submit.prevent="submitForm">
      <v-row>
        <v-col cols="8" class="py-0">
          <e-label>Aluno</e-label>

          <v-text-field
            id="historico-aluno"
            data-testid="historico-aluno"
            v-model="selectedStudent"
            label="Aluno"
            dense
            solo
            disabled
          />
        </v-col>

        <v-col cols="4" class="py-0">
          <e-label>Identificação única(INEP)</e-label>

          <v-text-field
            id="historico-inep"
            data-testid="historico-inep"
            v-model="inep"
            label="INEP"
            type="text"
            dense
            solo
            :maxlength="TAMANHO_INEP"
            :counter="TAMANHO_INEP"
            :rules="[requiredRule('INEP')]"
            @keypress="(event) => bloquearCaractere(event, CLASSE_NUMERO)"
            @compositionend.native="
              (event) => sanitizarNumero('inep', event.target.value, TAMANHO_INEP)
            "
            @beforeinput="bloquearEmoji"
            @input="(valor) => sanitizarNumero('inep', valor, TAMANHO_INEP)"
          />
        </v-col>

        <v-col cols="4" class="py-0">
          <e-label>Escola</e-label>

          <v-text-field
            id="historico-escola"
            data-testid="historico-escola"
            v-model="escola"
            label="Escola"
            dense
            solo
            :maxlength="TAMANHO_TEXTO"
            :counter="TAMANHO_TEXTO"
            :rules="[requiredRule('Escola')]"
            :error-messages="errosDeCaractere.escola"
            @keypress="(event) => bloquearCaractere(event, CLASSE_NOME)"
            @compositionend.native="(event) => sanitizarNome('escola', event.target.value)"
            @beforeinput="bloquearEmoji"
            @input="(valor) => sanitizarNome('escola', valor)"
          />
        </v-col>

        <v-col cols="4" class="py-0">
          <e-label>Estado</e-label>

          <v-autocomplete
            id="historico-estado"
            data-testid="historico-estado"
            v-model="estado"
            label="UF"
            dense
            solo
            :items="estadosList"
            :filter="searchQuery"
            :rules="[requiredRule('Estado')]"
            @change="limparMunicipio"
          />
        </v-col>

        <v-col cols="4" class="py-0">
          <e-label>Município</e-label>

          <v-autocomplete
            id="historico-municipio"
            data-testid="historico-municipio"
            v-model="municipio"
            label="Município"
            dense
            solo
            :items="municipiosList"
            :filter="searchQuery"
            :rules="[requiredRule('Munícipio')]"
            no-data-text="Selecione o estado"
          />
        </v-col>

        <v-col cols="4" class="py-0">
          <e-label>Ano de conclusão</e-label>

          <v-text-field
            id="historico-ano"
            data-testid="historico-ano"
            v-model="ano"
            label="Ano"
            dense
            solo
            :maxlength="TAMANHO_ANO"
            :counter="TAMANHO_ANO"
            :rules="[requiredRule('Ano de Conclusão')]"
            @keypress="(event) => bloquearCaractere(event, CLASSE_NUMERO)"
            @compositionend.native="
              (event) => sanitizarNumero('ano', event.target.value, TAMANHO_ANO)
            "
            @beforeinput="bloquearEmoji"
            @input="(valor) => sanitizarNumero('ano', valor, TAMANHO_ANO)"
          />
        </v-col>

        <v-col cols="4" class="py-0">
          <e-label>Série</e-label>

          <v-select
            id="historico-serie"
            data-testid="historico-serie"
            v-model="serie_id"
            label="Série"
            dense
            solo
            :items="seriesList"
            :rules="[requiredRule('Série')]"
            item-text="descricao"
            item-value="id"
            :return-object="false"
            @change="handleSerieChange"
          />
        </v-col>

        <v-col cols="4" class="py-0">
          <e-label>Modalidade</e-label>

          <v-text-field
            id="historico-modalidade"
            data-testid="historico-modalidade"
            v-model="modalidade"
            label="Ensino Regular / EJA"
            dense
            solo
            :maxlength="TAMANHO_TEXTO"
            :counter="TAMANHO_TEXTO"
            :rules="[requiredRule('Modalidade')]"
            :error-messages="errosDeCaractere.modalidade"
            @keypress="(event) => bloquearCaractere(event, CLASSE_NOME)"
            @compositionend.native="(event) => sanitizarNome('modalidade', event.target.value)"
            @beforeinput="bloquearEmoji"
            @input="(valor) => sanitizarNome('modalidade', valor)"
          />
        </v-col>

        <v-col cols="12" class="py-0">
          <div class="d-flex align-center justify-space-between">
            <e-label>Disciplinas e Médias</e-label>

            <v-btn
              id="historico-adicionar-disciplina"
              data-testid="historico-adicionar-disciplina"
              color="primary"
              text
              small
              @click="adicionarDisciplina"
            >
              <v-icon left x-small>fa fa-plus</v-icon>
              Adicionar disciplina
            </v-btn>
          </div>

          <v-alert v-if="!medias.length" dense text type="info" class="mt-2">
            A série define apenas a grade base. Selecione uma série para carregá-la ou adicione as
            disciplinas manualmente.
          </v-alert>

          <v-row v-for="(item, index) in medias" :key="item.uid" dense>
            <v-col cols="7" class="py-0">
              <v-text-field
                :id="`historico-disciplina-${index}`"
                :data-testid="`historico-disciplina-${index}`"
                v-model="item.descricao"
                label="Disciplina"
                dense
                solo
                :maxlength="TAMANHO_TEXTO"
                :rules="[requiredRule('Disciplina'), disciplinaUnicaRule]"
                :error-messages="item.erroDescricao"
                @keypress="(event) => bloquearCaractere(event, CLASSE_NOME)"
                @compositionend.native="(event) => sanitizarDisciplina(index, event.target.value)"
                @beforeinput="bloquearEmoji"
                @input="(valor) => sanitizarDisciplina(index, valor)"
              />
            </v-col>

            <v-col cols="3" class="py-0">
              <v-text-field
                :id="`historico-media-${index}`"
                :data-testid="`historico-media-${index}`"
                v-model="item.media"
                label="Média"
                type="text"
                inputmode="decimal"
                dense
                solo
                :maxlength="TAMANHO_MEDIA"
                :rules="[requiredRule('Média'), mediaValidaRule]"
                @keypress="(event) => bloquearCaractere(event, CLASSE_NOTA)"
                @compositionend.native="(event) => sanitizarNota(index, event.target.value)"
                @beforeinput="bloquearEmoji"
                @input="(valor) => sanitizarNota(index, valor)"
              />
            </v-col>

            <v-col cols="2" class="py-0">
              <v-btn
                :id="`historico-remover-disciplina-${index}`"
                :data-testid="`historico-remover-disciplina-${index}`"
                icon
                color="error"
                title="Remover disciplina"
                @click="removerDisciplina(index)"
              >
                <v-icon small>fa fa-trash</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="12" class="py-0">
          <v-btn
            id="historico-salvar"
            data-testid="historico-salvar"
            block
            color="primary"
            type="submit"
          >
            Salvar
          </v-btn>
        </v-col>
      </v-row>
    </v-form>

    <v-dialog v-model="dialogSubstituirGrade" max-width="480" persistent>
      <v-card>
        <v-card-title class="text-h6"> Substituir disciplinas? </v-card-title>

        <v-card-text>
          Já existem disciplinas preenchidas. Deseja substituí-las pela grade base da série
          selecionada? As médias digitadas serão perdidas.
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            id="historico-manter-disciplinas"
            data-testid="historico-manter-disciplinas"
            text
            @click="manterDisciplinas"
          >
            Manter atuais
          </v-btn>

          <v-btn
            id="historico-substituir-disciplinas"
            data-testid="historico-substituir-disciplinas"
            text
            color="primary"
            @click="confirmarSubstituicaoGrade"
          >
            Substituir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogNotaInvalida" max-width="400">
      <v-card>
        <v-card-title class="text-h6"> Valor inválido </v-card-title>

        <v-card-text>
          A média máxima permitida é {{ MEDIA_MAXIMA }}. Use vírgula para notas quebradas (ex.:
          9,5).
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            id="historico-nota-invalida-ok"
            data-testid="historico-nota-invalida-ok"
            text
            color="primary"
            @click="dialogNotaInvalida = false"
          >
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </main-template>
</template>

<script>
import searchQuery from "@/plugins/searchQuery";

const CLASSE_NOME = "A-Za-zÀ-ÖØ-öø-ÿ0-9 .,'\\-&/()ºª©®™";
const CLASSE_NUMERO = "0-9";
const CLASSE_NOTA = "0-9.,";

const CURSO_ID_MEDIO = 7;
const CURSOS_ID_FUNDAMENTAL = [4, 5];

const CASAS_DECIMAIS_MEDIA = 1;
const MEDIA_MAXIMA = 10;
const TAMANHO_ANO = 4;
const TAMANHO_INEP = 20;
const TAMANHO_MEDIA = 4;
const TAMANHO_TEXTO = 255;

const REGEX_MEDIA = /^\d{1,2}([,.]\d?)?$/;

const MENSAGEM_CARACTERE_REMOVIDO = "Caracteres não permitidos foram removidos.";
const REGEX_EMOJI =
  /[\uD800-\uDFFF]|[\u2190-\u21FF\u2300-\u27BF\u2934\u2935\u2B00-\u2BFF\u3030\u303D\u3297\u3299]|\uFE0F|\u200D|\u20E3/g;

const removerCaracteresInvalidos = (valor, classe) =>
  `${valor ?? ""}`.replace(new RegExp(`[^${classe}]`, "g"), "");

const removerEmojis = (valor) => `${valor ?? ""}`.replace(REGEX_EMOJI, "");

const normalizarDescricao = (valor) => `${valor ?? ""}`.trim().toLowerCase().replace(/\s+/g, " ");

/**
 * Nota exibida no formulário: vírgula como separador decimal e no máximo uma
 * casa, o mesmo formato usado na impressão do histórico.
 */
const normalizarNotaDigitada = (valor) => {
  const digitos = `${valor ?? ""}`.replace(new RegExp(`[^${CLASSE_NOTA}]`, "g"), "");
  const [inteira, ...resto] = digitos.replace(/\./g, ",").split(",");

  if (!resto.length) return inteira.slice(0, TAMANHO_MEDIA);

  const decimal = resto.join("").slice(0, CASAS_DECIMAIS_MEDIA);

  return `${inteira || "0"},${decimal}`.slice(0, TAMANHO_MEDIA);
};

/**
 * Converte a nota digitada ("9,5") em número, ou null quando ainda não há
 * valor avaliável ("", ",", "9,").
 */
const notaParaNumero = (valor) => {
  const texto = `${valor ?? ""}`.replace(",", ".").replace(/\.$/, "");
  if (texto === "") return null;

  const numero = Number(texto);

  return Number.isFinite(numero) ? numero : null;
};

/** Formato aceito pela API: ponto como separador decimal. */
const notaParaEnvio = (valor) => {
  const numero = notaParaNumero(valor);

  return numero === null ? "" : `${numero}`;
};

/** Notas salvas chegam com ponto ("9.50"); o formulário exibe "9,5". */
const notaParaExibicao = (valor) => {
  const numero = notaParaNumero(valor);

  return numero === null ? "" : `${numero}`.replace(".", ",");
};

export default {
  name: "HistoricoForm",

  props: {
    cursoTipo: {
      type: String,
      default: "ensino_fundamental",
    },
    editing: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      allSeries: [],
      aluno_id: "",
      ano: "",
      CLASSE_NOME,
      CLASSE_NOTA,
      CLASSE_NUMERO,
      curso: "fundamental",
      dialogNotaInvalida: false,
      dialogSubstituirGrade: false,
      ensino: null,
      errosDeCaractere: {
        escola: [],
        modalidade: [],
      },
      escola: "",
      estado: "",
      id: "",
      inep: "",
      medias: [],
      MEDIA_MAXIMA,
      modalidade: "",
      municipio: "",
      proximoUid: 0,
      searchQuery,
      selectedStudent: "",
      serie_id: null,
      serieParaCarregar: null,
      TAMANHO_ANO,
      TAMANHO_INEP,
      TAMANHO_MEDIA,
      TAMANHO_TEXTO,
    };
  },

  computed: {
    pageTitle() {
      return this.editing ? "Editando" : "Criando";
    },

    /**
     * Descrições repetidas entre as linhas: cada disciplina precisa ser única
     * porque o histórico consolida as médias por disciplina.
     */
    descricoesDuplicadas() {
      const contagem = this.medias.reduce((acumulado, item) => {
        const chave = normalizarDescricao(item.descricao);
        if (chave) acumulado[chave] = (acumulado[chave] ?? 0) + 1;

        return acumulado;
      }, {});

      return new Set(Object.keys(contagem).filter((chave) => contagem[chave] > 1));
    },

    estadosList() {
      const siglas = this.$constants.estadosList.map((estado) => estado.codigo);

      return this.estado && !siglas.includes(this.estado) ? [this.estado, ...siglas] : siglas;
    },

    municipiosList() {
      const cidades = this.$constants.municipios[this.estado]?.cidades ?? [];

      return this.municipio && !cidades.includes(this.municipio)
        ? [this.municipio, ...cidades]
        : cidades;
    },

    fullTitle() {
      const base = `${this.pageTitle} Histórico Acadêmico`;
      return base;
    },

    resolvedEnsino() {
      return this.ensino || this.resolveEnsino(this.cursoTipo);
    },

    seriesList() {
      const cursosPermitidos =
        this.resolvedEnsino === "ensino_medio" ? [CURSO_ID_MEDIO] : CURSOS_ID_FUNDAMENTAL;

      const seriesFiltradas = this.allSeries.filter(
        (serie) =>
          cursosPermitidos.includes(serie.curso_id) &&
          Array.isArray(serie.disciplinas) &&
          serie.disciplinas.length > 0
      );

      const seriesPorDescricao = new Map();

      seriesFiltradas.forEach((serie) => {
        const chave = (serie.descricao ?? "")
          .trim()
          .toLowerCase()
          .replace(/\s+/g, " ")
          .replace(/°/g, "º");
        if (!seriesPorDescricao.has(chave)) {
          seriesPorDescricao.set(chave, serie);
        }
      });

      return Array.from(seriesPorDescricao.values());
    },
  },

  async mounted() {
    await this.getSeries();

    if (this.editing) {
      await this.loadEditingData();
      return;
    }

    await this.loadNewData();
  },

  methods: {
    adicionarDisciplina() {
      this.medias.push(this.criarLinhaDeMedia());
    },

    bloquearCaractere(event, classe) {
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const tecla = event.key;
      if (!tecla || tecla.length !== 1) return;

      if (!new RegExp(`^[${classe}]$`).test(tecla)) {
        event.preventDefault();
      }
    },

    bloquearEmoji(event) {
      if (event.data && /\p{Extended_Pictographic}/u.test(event.data)) {
        event.preventDefault();
      }
    },

    buildHistoricoObject() {
      return {
        aluno_id: this.aluno_id,
        ano: this.ano,
        escola: this.escola,
        estado: this.estado,
        id: this.id,
        inep: this.inep,
        medias: this.medias.map((item) => ({
          disciplina_id: item.disciplina_id ?? null,
          descricao: `${item.descricao ?? ""}`.trim(),
          media: notaParaEnvio(item.media),
        })),
        modalidade: this.modalidade,
        municipio: this.municipio,
        serie_id: this.serie_id,
      };
    },

    /**
     * Carrega a grade da série como ponto de partida, substituindo as linhas
     * atuais. Disciplinas de outras escolas podem ser ajustadas depois.
     */
    async carregarGradeDaSerie(serie_id) {
      try {
        const disciplinas = await this.$services.seriesService.getDisciplinasBySerie(serie_id);

        this.medias = disciplinas.map((disciplina) =>
          this.criarLinhaDeMedia({
            disciplina_id: disciplina.id,
            descricao: disciplina.descricao,
          })
        );
      } catch (error) {
        this.$handleError(error.error || error);
      }
    },

    confirmarSubstituicaoGrade() {
      const serie_id = this.serieParaCarregar;

      this.dialogSubstituirGrade = false;
      this.serieParaCarregar = null;

      if (serie_id) this.carregarGradeDaSerie(serie_id);
    },

    /**
     * Linha da lista de médias. `disciplina_id` fica nulo nas disciplinas
     * digitadas manualmente, que não existem no cadastro da rede.
     */
    criarLinhaDeMedia({ disciplina_id = null, descricao = "", media = "" } = {}) {
      this.proximoUid += 1;

      return {
        disciplina_id,
        descricao,
        erroDescricao: [],
        media,
        uid: this.proximoUid,
      };
    },

    disciplinaUnicaRule(valor) {
      const chave = normalizarDescricao(valor);

      return chave && this.descricoesDuplicadas.has(chave) ? "Disciplina repetida" : true;
    },

    async fetchHistorico() {
      try {
        const { historico_id } = this.$route.params;
        const historico = await this.$services.historicoService.pegarHistorico(historico_id);
        return historico?.[0]?.[0] ?? null;
      } catch (error) {
        this.$handleError(error);
        return null;
      }
    },

    async getSeries() {
      try {
        const series = await this.$services.seriesService.syncAll();
        this.allSeries = Array.isArray(series) ? series : [];
      } catch (error) {
        this.$handleError(error);
      }
    },

    /**
     * A série é apenas a base da grade: se o usuário já montou a lista,
     * confirma antes de descartá-la.
     */
    async handleSerieChange(serie_id) {
      if (!serie_id) return;

      if (this.medias.some((item) => `${item.descricao ?? ""}`.trim() || item.media)) {
        this.serieParaCarregar = serie_id;
        this.dialogSubstituirGrade = true;
        return;
      }

      await this.carregarGradeDaSerie(serie_id);
    },

    handleBackNavigation() {
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push({
          name: "historico.index",
          params: { aluno_id: this.aluno_id },
        });
      }
    },

    limparMunicipio() {
      this.municipio = "";
    },

    manterDisciplinas() {
      this.dialogSubstituirGrade = false;
      this.serieParaCarregar = null;
    },

    async loadEditingData() {
      const historico = await this.fetchHistorico();
      if (!historico) return;

      this.ano = historico.ano;
      this.aluno_id = historico.aluno.id;
      this.escola = historico.escola;
      this.estado = historico.estado;
      this.id = historico.id;
      this.inep = historico.inep;
      this.modalidade = historico.modalidade;
      this.municipio = historico.municipio;
      this.selectedStudent = historico.aluno.nomecompleto;
      this.serie_id = Number(historico.serie_id);
      this.resolveEnsinoBySerie(this.serie_id);

      await this.carregarGradeDaSerie(this.serie_id);

      if (!Array.isArray(historico.medias) || !historico.medias.length) return;

      const gradeBase = this.medias;

      this.medias = historico.medias.map((media) => {
        const base = gradeBase.find(
          (item) => Number(item.disciplina_id) === Number(media.disciplina_id)
        );

        return this.criarLinhaDeMedia({
          disciplina_id: media.disciplina_id ?? null,
          descricao: `${media.descricao ?? ""}`.trim() || base?.descricao || "",
          media: notaParaExibicao(media.media),
        });
      });
    },

    async loadNewData() {
      try {
        const aluno = await this.$services.historicoService.pegarAluno(this.$route.params.aluno_id);

        this.selectedStudent = aluno.nomecompleto;
        this.aluno_id = aluno.id;
      } catch (error) {
        this.$handleError(error);
      }
    },

    removerDisciplina(index) {
      this.medias.splice(index, 1);
    },

    resolveEnsino(value) {
      const raw = (value || "").toString().toLowerCase();
      return raw.includes("médio") || raw.includes("medio") ? "ensino_medio" : "fundamental";
    },

    resolveEnsinoBySerie(serieId) {
      const serie = this.findSerie(serieId);
      if (!serie) return;

      this.ensino = serie.curso_id === CURSO_ID_MEDIO ? "ensino_medio" : "fundamental";
    },

    findSerie(serieId) {
      return this.allSeries.find((item) => Number(item.id) === Number(serieId));
    },

    sanitizarDisciplina(index, valor) {
      const linha = this.medias[index];
      if (!linha) return;

      const limpo = removerCaracteresInvalidos(removerEmojis(valor), CLASSE_NOME).slice(
        0,
        TAMANHO_TEXTO
      );

      linha.erroDescricao = limpo === `${valor ?? ""}` ? [] : [MENSAGEM_CARACTERE_REMOVIDO];
      linha.descricao = limpo;
    },

    sanitizarNome(campo, valor) {
      const limpo = removerCaracteresInvalidos(removerEmojis(valor), CLASSE_NOME);

      this.errosDeCaractere[campo] =
        limpo === `${valor ?? ""}` ? [] : [MENSAGEM_CARACTERE_REMOVIDO];
      this[campo] = limpo;
    },

    sanitizarNumero(campo, valor, tamanho) {
      this[campo] = removerCaracteresInvalidos(valor, CLASSE_NUMERO).slice(0, tamanho);
    },

    sanitizarNota(index, valor) {
      const linha = this.medias[index];
      if (!linha) return;

      const nota = normalizarNotaDigitada(valor);
      const numero = notaParaNumero(nota);

      if (numero !== null && numero > MEDIA_MAXIMA) {
        this.dialogNotaInvalida = true;
        linha.media = "";
        return;
      }

      linha.media = nota;
    },

    /**
     * Notas de 0 a 10, com até uma casa decimal separada por vírgula.
     */
    mediaValidaRule(valor) {
      const texto = `${valor ?? ""}`;
      if (texto === "") return true;

      if (!REGEX_MEDIA.test(texto)) return "Informe a média no formato 9 ou 9,5";

      const numero = notaParaNumero(texto);

      return numero !== null && numero > MEDIA_MAXIMA
        ? `A média máxima permitida é ${MEDIA_MAXIMA}`
        : true;
    },

    requiredRule(fieldName) {
      return (value) =>
        value !== null && value !== undefined && value !== ""
          ? true
          : `O campo ${fieldName} é obrigatório`;
    },

    async submitForm() {
      const erro = await this.validationError();
      if (erro) {
        this.$toast.danger(erro);
        return;
      }

      this.$loaderService.open("Salvando dados");

      const historico = this.buildHistoricoObject();

      try {
        if (this.editing) {
          await this.$services.historicoService.atualizarHistorico(historico);
        } else {
          await this.$services.historicoService.criarHistorico(historico);
        }

        this.updateCourseType();
        this.$router.push({
          name: "historico.index",
          params: {
            aluno_id: this.aluno_id,
            curso: this.curso,
          },
        });
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.$loaderService.close();
      }
    },

    updateCourseType() {
      const serie = this.findSerie(this.serie_id);
      this.curso = serie?.curso_id === CURSO_ID_MEDIO ? "ensino_medio" : "fundamental";
    },

    /**
     * Mensagem do primeiro problema encontrado, ou null quando está válido.
     */
    async validationError() {
      await this.$nextTick();

      if (!this.$refs.form?.validate()) return "Preencha todos os campos obrigatórios";
      if (!this.serie_id) return "Selecione a série";
      if (!this.medias.length) return "Adicione ao menos uma disciplina";

      if (this.medias.some((item) => !`${item?.descricao ?? ""}`.trim())) {
        return "Informe a descrição de todas as disciplinas";
      }

      if (this.medias.some((item) => `${item?.media ?? ""}` === "")) {
        return "Informe a média de todas as disciplinas";
      }

      if (this.descricoesDuplicadas.size) return "Existem disciplinas repetidas na lista";

      return null;
    },
  },
};
</script>
